import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Typography,
  Box,
  Alert,
  Skeleton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PaperSection from '../../components/PaperSection';
import { Invoice, InvoiceStatus, UserRole } from '@fsp/types';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { ArrowUpward, Settings } from '@mui/icons-material';

export const InvoiceRoles = [UserRole.fsp, UserRole.OWNER, UserRole.ADMIN];

const Invoices: React.FC = () => {
  const { organisationId } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [noInvoices, setNoInvoices] = useState<boolean>(false);
  const navigate = useNavigate();

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(`/organisations/${organisationId}/stripe/invoices`);

      // Handle no content response (no invoices)
      if (response.status === 204) {
        setNoInvoices(true);
        setInvoices([]);
        return;
      }

      // Handle not found (no invoices)
      if (response.status === 404) {
        setNoInvoices(true);
        setInvoices([]);
        return;
      }

      if (!response.ok) {
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        const errorData = await response.json().catch(() => null);
        if (errorData?.message) {
          throw new Error(errorData.message);
        }
        // If no specific error message and not a server error, treat as no invoices
        setNoInvoices(true);
        setInvoices([]);
        return;
      }

      const result = await response.json();
      const invoicesData: Invoice[] = Array.isArray(result.invoices) ? result.invoices : [];

      if (invoicesData.length === 0) {
        setNoInvoices(true);
      } else {
        setNoInvoices(false);
      }

      setInvoices(invoicesData);
    } catch (err: any) {
      if (err.message && err.message !== 'Failed to fetch invoices') {
        setError(err.message);
      } else if (err.name === 'NetworkError' || err.name === 'TypeError') {
        setError('Unable to connect to server. Please check your connection.');
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const getStatusColor = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.PAID:
        return 'success';
      case InvoiceStatus.OPEN:
      case InvoiceStatus.DRAFT:
        return 'warning';
      case InvoiceStatus.UNCOLLECTIBLE:
        return 'error';
      case InvoiceStatus.VOID:
        return 'default';
    }
  };

  if (loading) {
    return (
      <PaperSection title="Invoice History">
        <InvoicesSkeleton />
      </PaperSection>
    );
  }

  return (
    <PaperSection title="Invoice History">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {noInvoices || invoices.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">No invoices yet</Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Number</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    {invoice.created ? new Date(invoice.created).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell>{invoice.number}</TableCell>
                  <TableCell>${invoice.total?.toFixed(2)}</TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.status}
                      size="small"
                      color={getStatusColor(invoice.status)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {invoice.invoicePdf && (
                      <IconButton
                        size="small"
                        component="a"
                        href={invoice.invoicePdf}
                        download={`invoice-${invoice.id || 'document'}.pdf`}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </PaperSection>
  );
};

export default Invoices;

export const InvoicesSkeleton: React.FC = () => {
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Invoice</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[1, 2, 3, 4, 5].map((index) => (
            <TableRow key={index}>
              <TableCell>
                <Skeleton variant="text" width={120} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={80} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={150} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={60} />
              </TableCell>
              <TableCell>
                <Skeleton variant="rounded" width={70} height={24} />
              </TableCell>
              <TableCell align="right">
                <Box display="flex" justifyContent="flex-end">
                  <Skeleton variant="circular" width={32} height={32} />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
